import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Review {
  id: string;
  author: string;
  avatar: string;
  rating: number;
  review_date: string;
  title: string;
  content: string;
}

interface TripAdvisorReviewsProps {
  reviews: Review[];
  loading?: boolean;
}

export default function TripAdvisorReviews({ reviews, loading }: TripAdvisorReviewsProps) {
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const { t } = useTranslation();

  const toggleExpand = (id: string) => {
    setExpandedReviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section className="w-full bg-primary-100 py-12 md:py-16 lg:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <p className="text-sm md:text-base font-medium text-secondary-600 uppercase tracking-wider mb-2">
            {t('reviews.howEarn')}
          </p>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-primary-700 mb-3">
            {t('reviews.star')}
          </h2>
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm md:text-base text-secondary-600">{t('reviews.on')}</span>
            <div className="flex items-center gap-1">
              <div className="w-5 h-5 md:w-6 md:h-6 bg-primary-500 rounded-full flex items-center justify-center">
                <i className="ri-star-fill text-white text-xs" />
              </div>
              <span className="font-semibold text-secondary-800 text-sm md:text-base">Tripadvisor</span>
            </div>
          </div>
        </div>

        {/* Loading / Empty */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-secondary-100 animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-100" />
                  <div className="space-y-2">
                    <div className="h-3 bg-secondary-100 rounded w-24" />
                    <div className="h-2 bg-secondary-100 rounded w-16" />
                  </div>
                </div>
                <div className="h-4 bg-secondary-100 rounded w-3/4 mb-2" />
                <div className="space-y-2">
                  <div className="h-3 bg-secondary-100 rounded" />
                  <div className="h-3 bg-secondary-100 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-secondary-500 text-sm">{t('reviews.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white rounded-xl p-5 border border-secondary-100 hover:shadow-md transition-shadow"
              >
                {/* Author */}
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={review.avatar}
                    alt={review.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">{review.author}</p>
                    <p className="text-xs text-secondary-500">{review.review_date}</p>
                  </div>
                </div>

                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`ri-star-fill text-sm ${
                        i < review.rating ? 'text-accent-400' : 'text-secondary-200'
                      }`}
                    />
                  ))}
                </div>

                {/* Title & Content */}
                <h4 className="font-medium text-secondary-900 text-sm mb-2">{review.title}</h4>
                <p className="text-sm text-secondary-600 leading-relaxed">
                  {expandedReviews.has(review.id)
                    ? review.content
                    : review.content.slice(0, 120) + '...'}
                </p>
                {review.content.length > 120 && (
                  <button
                    onClick={() => toggleExpand(review.id)}
                    className="text-primary-600 text-sm font-medium mt-2 hover:underline"
                  >
                    {expandedReviews.has(review.id) ? t('reviews.showLess') : t('reviews.readMore')}
                  </button>
                )}

                {/* Tripadvisor badge */}
                <div className="flex items-center gap-1 mt-4 pt-3 border-t border-secondary-100">
                  <div className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                    <i className="ri-star-fill text-white text-[8px]" />
                  </div>
                  <span className="text-xs text-secondary-500">{t('reviews.postedOn')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}